import { FunctionComponent, useMemo } from 'react';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SuccessScreen from '../../UI/V3/SuccessScreen';
import Button from '../../UI/V3/Button/Button';
import { LocationCopyAreaOption, LocationDuplication, LOCATION_COPY_AREAS } from '../../../model/location';
import styles from './DuplicateLocationModal.module.scss';

interface ReviewGroup {
    area: LocationCopyAreaOption;
    messages: Array<string>;
}

const NAME_TAKEN_MESSAGE = 'A location with this name already exists. Choose a different name to continue.';

// The job reports a name conflict as a plain message ("Business name has already been taken")
const isNameTaken = (error: string | null): boolean =>
    error !== null && /name/i.test(error) && /(taken|exists)/i.test(error);

interface DuplicateLocationResultProps {
    locationName: string;
    result: LocationDuplication;
    onViewLocation: (locationId: number) => void;
    onBackToForm: () => void;
    onClose: () => void;
}

export const DuplicateLocationResult: FunctionComponent<DuplicateLocationResultProps> = ({
    locationName,
    result,
    onViewLocation,
    onBackToForm,
    onClose
}) => {
    // Keeps the checklist order and labels
    const copiedAreas = useMemo(
        () => LOCATION_COPY_AREAS.filter(area => result.copied.includes(area.id)),
        [result.copied]
    );

    const reviewGroups = useMemo<Array<ReviewGroup>>(
        () =>
            LOCATION_COPY_AREAS.map(area => ({
                area,
                messages: result.review.filter(item => item.area === area.id).map(item => item.message)
            })).filter(group => group.messages.length > 0),
        [result.review]
    );

    if (result.status === 'failed') {
        return (
            <div className={styles.statusScreen}>
                <span className={styles.errorIcon}>
                    <FontAwesomeIcon icon={faXmark} />
                </span>
                <span className={styles.statusTitle}>Duplication failed</span>
                <p className={styles.statusMessage}>
                    {isNameTaken(result.error) ? (
                        NAME_TAKEN_MESSAGE
                    ) : (
                        <>
                            {result.error && (
                                <>
                                    {result.error}
                                    <br />
                                </>
                            )}
                            No location was created. You can go back to the form and try again.
                        </>
                    )}
                </p>
                <div className={styles.actions}>
                    <Button
                        label="Close"
                        variant="outlined"
                        size="large"
                        onClick={onClose}
                    />
                    <Button
                        label="Back to form"
                        variant="black"
                        size="large"
                        onClick={onBackToForm}
                    />
                </div>
            </div>
        );
    }

    const marketplaceId = result.marketplaceId;

    return (
        <SuccessScreen
            className={styles.summary}
            title="Location duplicated"
            buttonLabel={marketplaceId !== null ? 'View location' : 'Close'}
            onClick={marketplaceId !== null ? () => onViewLocation(marketplaceId) : onClose}
            message={
                <div className={styles.summaryContent}>
                    <p className={styles.statusMessage}>
                        <b>{locationName}</b> was created.
                    </p>

                    <div className={styles.summarySection}>
                        <span className={styles.fieldLabel}>Copied</span>
                        {copiedAreas.length > 0 ? (
                            <ul className={styles.summaryList}>
                                {copiedAreas.map(area => (
                                    <li key={area.id}>
                                        <FontAwesomeIcon
                                            icon={faCircleCheck}
                                            className={styles.copiedIcon}
                                        />
                                        <span>{area.label}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className={styles.summaryEmpty}>Nothing was copied.</p>
                        )}
                    </div>

                    <div className={styles.summarySection}>
                        <span className={styles.fieldLabel}>Worth reviewing</span>
                        {reviewGroups.length > 0 ? (
                            <ul className={styles.summaryList}>
                                {reviewGroups.map(group => (
                                    <li
                                        key={group.area.id}
                                        className={styles.reviewItem}
                                    >
                                        <FontAwesomeIcon
                                            icon={faTriangleExclamation}
                                            className={styles.reviewIcon}
                                        />
                                        <div>
                                            <span className={styles.reviewArea}>{group.area.label}</span>
                                            {group.messages.map(message => (
                                                <p
                                                    key={message}
                                                    className={styles.reviewMessage}
                                                >
                                                    {message}
                                                </p>
                                            ))}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className={styles.summaryEmpty}>Nothing to review.</p>
                        )}
                    </div>
                </div>
            }
        />
    );
};

export default DuplicateLocationResult;
